'use es6';

import { createTreatmentsResolver } from 'laboratory-lib/core/resolvers';
import { createExposureLogger } from 'laboratory-lib/core/loggers';
import { parseExperiments } from 'laboratory-lib/core/parsers';
import { validateHttp, validateSearch, validateStore, validateTimeout, validateTreatmentsSchema, validateQuickFetch, validateQuickFetchLabel, validateExperimentsSchema, validateIdentifiersSchema } from 'laboratory-lib/core/validators';
const mockHttpClient = {
  get: () => new Promise((onSuccess, onError) => onError(new Error('no http client implemented'))),
  post: () => new Promise((onSuccess, onError) => onError(new Error('no http client implemented')))
};

function isPromise(thing) {
  return thing && typeof thing.then === 'function' && typeof thing.catch === 'function';
}

function promisify(thing) {
  if (isPromise(thing)) {
    return thing;
  }

  return new Promise(onComplete => onComplete(thing));
}

function adaptAsyncStore(store) {
  let asyncStore;
  return (method, ...args) => {
    if (!store) return promisify();

    if (!asyncStore && typeof store.open === 'function') {
      try {
        asyncStore = store.open();
      } catch (err) {
        asyncStore = promisify();
      }
    }

    return new Promise(onSuccess => {
      const storeTimeout = setTimeout(() => {
        onSuccess(promisify());
      }, 1000);
      asyncStore.then(openStore => {
        clearTimeout(storeTimeout);

        if (!openStore) {
          onSuccess(promisify());
        } else {
          onSuccess(promisify(openStore[method](...args)));
        }
      }).catch(() => {
        clearTimeout(storeTimeout);
        onSuccess(promisify());
      });
    });
  };
}

function validateClientInputs({
  caller,
  http,
  quickFetch,
  quickFetchLabel,
  search,
  store,
  timeout,
  treatments,
  experiments,
  identifiers
}) {
  validateHttp(http, caller);
  validateSearch(search, caller);
  validateStore(store, caller);
  validateTimeout(timeout, caller);

  if (treatments) {
    if (experiments) {
      throw new Error(`laboratory-lib "${caller}" expected either "treatments" or "experiments" to be present. not both.`);
    }

    validateTreatmentsSchema(treatments, caller);
  }

  if (experiments) {
    if (!identifiers) {
      throw new Error(`laboratory-lib "${caller}" expected "identifiers" to be present when passing "experiments".`);
    }

    validateIdentifiersSchema(identifiers, caller);
    validateExperimentsSchema({
      experiments,
      identifiers
    }, caller);
  }

  if (quickFetchLabel) {
    validateQuickFetch(quickFetch, caller);
    validateQuickFetchLabel(quickFetchLabel, caller);
  }
}

export function createClient({
  apiDomain = '',
  ceid = '',
  http = mockHttpClient,
  onError = () => {},
  search = window.location.search,
  store = null,
  quickFetch = null,
  quickFetchLabel = '',
  timeout = 0,
  treatments,
  experiments,
  identifiers
} = {}) {
  // throws runtime exceptions
  validateClientInputs({
    http,
    quickFetch,
    quickFetchLabel,
    search,
    store,
    timeout,
    treatments,
    experiments,
    identifiers,
    caller: 'createClient'
  });
  const schema = (experiments ? // if experiments are present, parses them into a treatments schema
  parseExperiments({
    experiments,
    identifiers
  }) : treatments) || {};
  const asyncStore = store ? adaptAsyncStore(store) : undefined;
  const {
    resolveTreatments
  } = createTreatmentsResolver({
    asyncStore,
    apiDomain,
    ceid,
    http,
    onError,
    quickFetch,
    quickFetchLabel,
    schema,
    identifiers,
    search,
    timeout
  });
  const {
    logExposure,
    logExposures
  } = createExposureLogger({
    apiDomain,
    http,
    resolveTreatments,
    onError
  });
  return {
    logExposure,
    logExposures,
    resolve: resolveTreatments
  };
}